
import { useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

const testimonials = [
  {
    id: 1,
    name: "Dr. Elena Moore",
    title: "Neuroscientist",
    quote: "As someone who studies the brain for a living, I'm incredibly impressed with SOLR's formulations. The cognitive enhancement is noticeable but balanced - exactly what a good nootropic should deliver.",
    avatar: "/placeholder.svg",
    rating: 5
  },
  {
    id: 2,
    name: "Jason Wright",
    title: "Software Engineer",
    quote: "I've tried dozens of focus drinks, and SOLR Cosmic Clarity is genuinely different. I can maintain deep work sessions for hours without the crash I get from energy drinks or coffee.",
    avatar: "/placeholder.svg",
    rating: 5
  },
  {
    id: 3,
    name: "Sophia Chen",
    title: "Creative Director",
    quote: "The Quantum Calm formula has been a game-changer for my creative process. It puts me in this perfect state of relaxed focus where ideas flow naturally without the anxiety.",
    avatar: "/placeholder.svg",
    rating: 4
  }
];

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };
  
  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const currentTestimonial = testimonials[currentIndex];
  
  return (
    <section className="py-20 relative">
      {/* Cosmic background effect */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
        <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full bg-solr-nebula-purple/30 filter blur-[100px]"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Neural <span className="neural-text">Journeys</span>
          </h2>
          <p className="text-solr-starfield-silver">
            Discover how SOLR has enhanced the cognitive experience and performance of our customers.
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="cosmic-card p-8 md:p-10 relative">
            {/* Neural connections */}
            <div className="absolute top-0 left-1/4 w-0 neural-connection animate-neural-connection"></div>
            <div className="absolute top-0 left-2/4 w-0 neural-connection animate-neural-connection" style={{ animationDelay: "0.5s" }}></div>
            <div className="absolute top-0 left-3/4 w-0 neural-connection animate-neural-connection" style={{ animationDelay: "1s" }}></div>
            
            <div className="text-center mb-8">
              {/* Star rating */}
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-5 h-5 ${
                      i < currentTestimonial.rating ? "text-solr-synapse-gold fill-solr-synapse-gold" : "text-solr-starfield-silver/30"
                    }`} 
                  />
                ))}
              </div>
              
              <p className="text-xl italic mb-8">
                "{currentTestimonial.quote}"
              </p>
              
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                  <img 
                    src={currentTestimonial.avatar} 
                    alt={currentTestimonial.name}
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="text-left">
                  <div className="font-semibold">{currentTestimonial.name}</div>
                  <div className="text-sm text-solr-starfield-silver">{currentTestimonial.title}</div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center mt-6 space-x-4">
              <button 
                onClick={goToPrevious}
                className="p-2 rounded-full border border-solr-nebula-purple/30 hover:bg-solr-nebula-purple/10 transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <button 
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full ${
                      index === currentIndex ? "bg-solr-nebula-purple" : "bg-solr-starfield-silver/30"
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              
              <button 
                onClick={goToNext}
                className="p-2 rounded-full border border-solr-nebula-purple/30 hover:bg-solr-nebula-purple/10 transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
